import * as React from "react";
import { Box, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { GridColDef, GridRowsProp } from "@mui/x-data-grid-premium";
import CclGenericAccordian from "../../../../../components/common/cclGenericAccordian";
import CclLoadingButton from "../../../../../components/common/cclButtons/cclLoadingButton";
import ReportsDataGridDataGridPanel from "./reportDataGrid";

export interface RealReviewProps {
    sessionId: string;
}

const RealReview: React.FC<RealReviewProps> = (props) => {
    const sessionId = props.sessionId;
    const [payPeriodEndDate, setPayPeriodEndDate] = React.useState<Date | null>(null);
    const [showData, setShowData] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const timeout = (delay: number) => new Promise(res => setTimeout(res, delay));

    const generateReport = async () => {
        setShowData(false);
        setLoading(true);
        await timeout(500);
        setShowData(true);
        setLoading(false);
    }

    const onDateChange = (date: Date | null) => {
        setPayPeriodEndDate(date);
    }

    const searchInputSx = {
        "& .MuiOutlinedInput-input": {
            paddingTop: "8px!important",
            paddingBottom: "8px!important",
        },
        "& .MuiInputLabel-root": {
            marginTop: "-8px!important",
        },
        "& .MuiInputLabel-shrink": {
            marginTop: "auto!important",
        },
        width: .25
    };

    const sharedColumns: GridColDef[] = [
        { field: "revRecDate", headerName: "Rev Rec Date", width: 150 },
        { field: "glText", headerName: "GLText", width: 150 },
        { field: "lob", headerName: "LOB", width: 150 },
        { field: "hours", headerName: "Hours", width: 150 },
        {
            field: "eventTrainer",
            headerName: "EventTrainer",
            width: 150,
            renderCell: (params) => {
                const hasEmployeeId = params.row?.employeeId != null;

                return (
                    <span
                        title={!hasEmployeeId ? "Missing Employee ID" : undefined}
                        style={{ fontWeight: !hasEmployeeId ? 700 : 400, textDecoration: !hasEmployeeId ? "underline" : "none" }}
                    >
                        {params.value}
                    </span>
                );
            },
        },
        {
            field: "calcFee",
            headerName: "CalcFee",
            width: 150,
            renderCell: (params) => {
                const isMismatch = Number(params.row?.calcFee) !== Number(params.row?.fee);

                return (
                    <span style={{ backgroundColor: isMismatch ? "#ffcdd2" : "inherit", padding: "4px 8px", borderRadius: "4px" }}>
                        {params.value}
                    </span>
                );
            },
        },
        { field: "fee", headerName: "Fee", width: 150 },
        { field: "chargeDate", headerName: "Charge Date", width: 150 },
        { field: "rateCode", headerName: "RateCode", width: 150 },
        { field: "units", headerName: "Units", width: 150 },
        { field: "eventCode", headerName: "EventCode", width: 150 },
    ];

    const be36Columns: GridColDef[] = [
        { field: "be36", headerName: "BE36", width: 170 },
        ...sharedColumns,
    ];

    const us00Columns: GridColDef[] = [
        { field: "us00", headerName: "US00", width: 170 },
        ...sharedColumns,
    ];

    const be36Data: GridRowsProp = [
        {
            id: "be36-1",
            be36: "BE36-1001",
            revRecDate: "2026-05-01",
            glText: "Facilitation Services",
            lob: "Leadership",
            hours: 8,
            eventTrainer: "Alex Carter",
            calcFee: 2400,
            fee: 2400,
            chargeDate: "2026-05-03",
            rateCode: "STD",
            units: 1,
            eventCode: "EVT-BE36-01",
            employeeId: "EMP-001",
        },
        {
            id: "be36-2",
            be36: "BE36-1002",
            revRecDate: "2026-05-04",
            glText: "Coaching Session",
            lob: "Coaching",
            hours: 6,
            eventTrainer: "Jordan Miles",
            calcFee: 1800,
            fee: 1800,
            chargeDate: "2026-05-05",
            rateCode: "STD",
            units: 1,
            eventCode: "EVT-BE36-02",
            employeeId: null,
        },
        {
            id: "be36-3",
            be36: "BE36-1003",
            revRecDate: "2026-05-07",
            glText: "Program Delivery",
            lob: "Enterprise",
            hours: 7.5,
            eventTrainer: "Taylor Reed",
            calcFee: 2250,
            fee: 2200,
            chargeDate: "2026-05-08",
            rateCode: "DISC",
            units: 1,
            eventCode: "EVT-BE36-03",
            employeeId: "EMP-003",
        },
        {
            id: "be36-4",
            be36: "BE36-1004",
            revRecDate: "2026-05-10",
            glText: "Assessment Debrief",
            lob: "Assessments",
            hours: 5,
            eventTrainer: "Morgan Lee",
            calcFee: 1500,
            fee: 1500,
            chargeDate: "2026-05-11",
            rateCode: "STD",
            units: 1,
            eventCode: "EVT-BE36-04",
            employeeId: "EMP-004",
        },
        {
            id: "be36-5",
            be36: "BE36-1005",
            revRecDate: "2026-05-13",
            glText: "Workshop Follow-up",
            lob: "Leadership",
            hours: 4,
            eventTrainer: "Casey Nguyen",
            calcFee: 1200,
            fee: 1150,
            chargeDate: "2026-05-14",
            rateCode: "DISC",
            units: 1,
            employeeId: null,
            eventCode: "EVT-BE36-05",
        },
    ];

    const us00Data: GridRowsProp = [
        {
            id: "us00-1",
            us00: "US00-2001",
            revRecDate: "2026-05-02",
            glText: "Facilitation Services",
            lob: "Leadership",
            hours: 8,
            eventTrainer: "Riley Brooks",
            calcFee: 2600,
            fee: 2600,
            chargeDate: "2026-05-04",
            rateCode: "PREM",
            units: 1,
            eventCode: "EVT-US00-01",
            employeeId: "EMP-101",
        },
        {
            id: "us00-2",
            us00: "US00-2002",
            revRecDate: "2026-05-05",
            glText: "Coaching Session",
            lob: "Coaching",
            hours: 6.5,
            eventTrainer: "Jamie Stone",
            calcFee: 1950,
            fee: 1950,
            chargeDate: "2026-05-06",
            rateCode: "STD",
            units: 1,
            eventCode: "EVT-US00-02",
            employeeId: null,
        },
        {
            id: "us00-3",
            us00: "US00-2003",
            revRecDate: "2026-05-08",
            glText: "Program Delivery",
            lob: "Enterprise",
            hours: 7,
            eventTrainer: "Quinn Harper",
            calcFee: 2100,
            fee: 2050,
            chargeDate: "2026-05-09",
            rateCode: "DISC",
            units: 1,
            eventCode: "EVT-US00-03",
            employeeId: "EMP-103",
        },
        {
            id: "us00-4",
            us00: "US00-2004",
            revRecDate: "2026-05-11",
            glText: "Assessment Debrief",
            lob: "Assessments",
            hours: 5.5,
            eventTrainer: "Avery Collins",
            calcFee: 1650,
            fee: 1650,
            chargeDate: "2026-05-12",
            rateCode: "STD",
            units: 1,
            employeeId: "EMP-104",
            eventCode: "EVT-US00-04",
        },
        {
            id: "us00-5",
            us00: "US00-2005",
            revRecDate: "2026-05-14",
            glText: "Workshop Follow-up",
            lob: "Leadership",
            hours: 4.5,
            eventTrainer: "Drew Bennett",
            calcFee: 1350,
            fee: 1300,
            chargeDate: "2026-05-15",
            rateCode: "DISC",
            units: 1,
            employeeId: null,
            eventCode: "EVT-US00-05",
        },
    ];

    return (
        <Box sx={{ width: "100%" }}>
            <CclGenericAccordian
                headingText={"Real Review Report"}
                defaultExpanded
            >
                <Stack sx={{margin: 1, padding: 1 }} spacing={2}>
                    <Stack direction={"row"} spacing={5} sx={{ mx: 2, mt: 2 }}>
                        <Typography sx={{ alignSelf: "center"}} variant="h6">Pay Period End Date</Typography>
                        <DatePicker
                            onChange={onDateChange}
                            value={payPeriodEndDate}
                            renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                                params.fullWidth = false;
                                return (
                                    <TextField
                                        {...params}
                                        sx={searchInputSx}
                                    />
                                );
                            }}
                        />
                        <CclLoadingButton
                            mode={"primary"}
                            sx={{marginLeft: "auto"}}
                            onClick={generateReport}
                            loading={loading}
                        >
                            Generate
                        </CclLoadingButton>
                    </Stack>
                    {showData && (
                        <Stack spacing={3}>
                            <Typography variant="h6">BE36</Typography>
                            <ReportsDataGridDataGridPanel
                                sessionId={sessionId}
                                columns={be36Columns}
                                data={be36Data}
                            />
                            <Typography variant="h6">US00</Typography>
                            <ReportsDataGridDataGridPanel
                                sessionId={sessionId}
                                columns={us00Columns}
                                data={us00Data}
                            />
                        </Stack>
                    )}
                </Stack>
            </CclGenericAccordian>
        </Box>
    );
};

export default RealReview;
